import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { MaskLines, EASE } from "./Motion";
import { IMG, PHONE_DISPLAY, PHONE_TEL } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";

const fade = (d) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE, delay: d },
});

const STATS = [["02", "Locations in Noida"], ["09", "Workspace formats"], ["1:1", "Direct owner access"]];

const HeroVisual = ({ rx, ry, py }) => (
  <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative mx-auto w-full max-w-[520px]">
    <motion.div
      initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
      transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
      className="relative aspect-[4/5] overflow-hidden rounded-[28px] border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,.8)]"
    >
      <motion.img src={IMG.building} alt="Kinara Works office building in Noida" style={{ y: py, scale: 1.18 }}
        className="h-full w-full object-cover" fetchPriority="high" data-testid="hero-image" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/85 via-transparent to-transparent" />
      <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
        <p className="font-mono-k text-[11px] uppercase tracking-[0.25em] text-slate-200">Noida · Uttar Pradesh</p>
        <span className="h-2 w-2 rounded-full bg-[#D4AF37] shadow-[0_0_18px_#D4AF37]" />
      </div>
    </motion.div>
    <motion.div {...fade(1.2)} style={{ transform: "translateZ(80px)" }}
      className="absolute -left-4 sm:-left-12 bottom-14 w-[42%] overflow-hidden rounded-2xl border border-white/15 shadow-2xl">
      <img src={IMG.desksA} alt="Workstations at Kinara Works" className="aspect-[4/5] w-full object-cover" />
    </motion.div>
    <motion.div {...fade(1.4)} style={{ transform: "translateZ(120px)" }}
      className="absolute -right-2 sm:-right-6 top-8 rounded-2xl border border-white/10 bg-[#131924]/80 backdrop-blur-xl px-4 py-3">
      <p className="font-mono-k text-[10px] uppercase tracking-[0.25em] text-slate-400">Now open for visits</p>
      <p className="font-display text-white text-sm mt-1">Sector 132 · Sector 90</p>
    </motion.div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const py = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const bigY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    mx.set(x - 0.5);
    my.set(y - 0.5);
    ref.current.style.setProperty("--mx", `${x * 100}%`);
    ref.current.style.setProperty("--my", `${y * 100}%`);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="hero-spot relative overflow-hidden bg-[#0B0F17] pt-28 pb-16 lg:pt-36 lg:pb-24 min-h-[100svh]">
      <div className="hero-grid absolute inset-0 opacity-[0.35]" aria-hidden="true" />
      <motion.div style={{ y: bigY }} aria-hidden="true"
        className="font-display pointer-events-none absolute -right-10 top-24 select-none text-[34vw] lg:text-[22vw] font-bold leading-none text-outline">
        132
      </motion.div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div {...fade(0.1)} data-testid="hero-highlight"
            className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.07] px-4 py-2 text-xs sm:text-sm text-[#F1DC8F]">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D4AF37] opacity-60" /><span className="relative h-2 w-2 rounded-full bg-[#D4AF37]" /></span>
            Two Premium Locations in Noida – Sector 132 &amp; Sector 90
          </motion.div>
          <MaskLines onLoad as="h1" testId="hero-title"
            className="font-display mt-8 text-[2.75rem] sm:text-6xl lg:text-[5.2rem] font-semibold leading-[0.98] tracking-[-0.035em] text-white"
            lines={["Premium", <>Co-Working <span className="text-gold">Spaces</span></>, "in Noida."]} />
          <motion.p {...fade(0.9)} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300">
            Flexible, professional and fully equipped workspaces designed for freelancers, startups, growing businesses and corporate teams.
          </motion.p>
          <motion.div {...fade(1.05)} className="mt-10 flex flex-wrap items-center gap-3">
            <button onClick={() => scrollToId("contact")} data-testid="hero-cta-book"
              className="btn-gold group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold">
              Book a Workspace <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </button>
            <button onClick={() => scrollToId("contact")} data-testid="hero-cta-visit"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm font-medium text-white hover:bg-white/5 hover:border-white/40 transition-colors">
              Schedule a Visit
            </button>
            <a href={PHONE_TEL} data-testid="hero-cta-call"
              className="inline-flex items-center gap-2 px-3 py-3.5 text-sm font-medium text-slate-200 hover:text-[#E3C45A] transition-colors">
              <Phone className="h-4 w-4 text-[#D4AF37]" /> Call {PHONE_DISPLAY}
            </a>
          </motion.div>
          <motion.dl {...fade(1.2)} className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-6">
            {STATS.map(([n, l]) => (
              <div key={l}>
                <dt className="font-display text-2xl sm:text-3xl text-white">{n}</dt>
                <dd className="mt-1 text-xs sm:text-sm text-slate-400">{l}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
        <div className="lg:col-span-5 [perspective:1200px] px-4 sm:px-10 lg:px-0">
          <HeroVisual rx={rx} ry={ry} py={py} />
        </div>
      </div>
    </section>
  );
};
